import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message } from 'antd';
import axiosInstance from '../../../axiosInstance';

const Accounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);

    const shopId = localStorage.getItem('shopId'); // Get shop ID once

    useEffect(() => {
        if (shopId) {
            fetchAccounts();
        } else {
            message.error('Shop ID not found. Please log in again.');
        }
    }, [shopId]);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/shops/${shopId}/account`);
            if (response.data) {
                setAccounts(response.data);
            } else {
                message.error('Failed to fetch accounts.');
                setAccounts([]);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch accounts.');
        } finally {
            setLoading(false);
        }
    };

    const showModal = (account = null) => {
        if (account) {
            setEditingAccount(account);
            form.setFieldsValue(account);
        } else {
            setEditingAccount(null);
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleOk = async (values) => {
        try {
            if (editingAccount) {
                await axiosInstance.put(`/shops/${shopId}/account/${editingAccount.id}`, values);
                message.success('Account updated successfully');
            } else {
                await axiosInstance.post(`/shops/${shopId}/account`, values);
                message.success('Account added successfully');
            }
            fetchAccounts();
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            console.error(error);
            message.error('Failed to save account.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/shops/${shopId}/account/${id}`);
            message.success('Account deleted successfully');
            fetchAccounts();
        } catch (error) {
            console.error(error);
            message.error('Failed to delete account.');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'account_name',
            key: 'account_name',
        },
        {
            title: 'Number',
            dataIndex: 'account_number',
            key: 'account_number',
        },
        {
            title: 'Code',
            dataIndex: 'account_code',
            key: 'account_code',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, account) => (
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button onClick={() => showModal(account)}>Edit</Button>
                    {/* Uncomment below to allow delete */}
                    {/* <Button danger onClick={() => handleDelete(account.id)}>Delete</Button> */}
                </div>
            ),
        },
    ];

    return (
        <div>
            <div style={{ textAlign: 'right', marginBottom: 16 }}>
                <Button type="primary" onClick={() => showModal()}>
                    Add Account
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={accounts}
                rowKey="id"
                pagination={{ pageSize: 25 }}
                loading={loading}
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title={editingAccount ? 'Edit Account' : 'Add New Account'}
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                okText={editingAccount ? 'Update' : 'Add'}
                cancelText="Cancel"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleOk}
                >
                    <Form.Item
                        name="account_name"
                        label="Account Name"
                        rules={[{ required: true, message: 'Please input the account name!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="account_number"
                        label="Account Number"
                        rules={[{ required: true, message: 'Please input the account number!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="account_code"
                        label="Account Code"
                        rules={[{ required: false }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="type"
                        label="Account Type"
                        rules={[{ required: true, message: 'Please select the account type!' }]}
                    >
                        <Select>
                            <Select.Option value="online">Online</Select.Option>
                            <Select.Option value="cash">Cash</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Accounts;
